import React, { useState, useEffect } from "react";
import { Box, Button, Modal, Text, Stack, Badge, Code, LoadingOverlay } from "@mantine/core";
import "@mantine/dropzone/styles.css"; // Lägger till Dropzone-stilar
import { usePostChecklistValidateExcelMutation } from "../../openapi";
import * as XLSX from "xlsx"; // Använder xlsx för konvertering
import { Dropzone } from "@mantine/dropzone";

const ChecklistExcelImportModal: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const [file, setFile] = useState<File | null>(null);
  const [postChecklistValidateExcel, { isLoading, error, isSuccess, data, reset }] =
    usePostChecklistValidateExcelMutation();

  useEffect(() => {
    setFile(null);
  }, []);

  const handleDrop = (files: File[]) => {
    setFile(files[0]);
  };

  const handleSubmit = async () => {
    if (file) {
      const arrayBuffer = await file.arrayBuffer();
      const workbook = XLSX.read(arrayBuffer, { type: "array" });
      const base64String = XLSX.write(workbook, { type: "base64", bookType: "xlsx" });
      postChecklistValidateExcel({ body: { file: base64String } }); // Skicka som base64-sträng
    }
  };

  const renderValidationResult = () => {
    if (isLoading) return <LoadingOverlay visible />;
    if (error)
      return (
        <Text c="red">
          Feil ved validering: {error instanceof Error ? error.message : "Ukjent feil"} <i className="fa fa-times" />
        </Text>
      );
    if (data && "status" in data && data.status === "error") {
      const errors = data.errors || [];
      return (
        <Stack>
          <Text weight={500}>Valideringsresultat (feil):</Text>
          <Code
            block
            style={{
              backgroundColor: "#ffcccc", // Ljus röd bakgrund för misslyckande
              color: "#800000", // Mörk röd text
              padding: "16px",
              borderRadius: "4px",
              border: "1px solid #ff0000",
            }}
          >
            {JSON.stringify(data, null, 2)}
          </Code>
        </Stack>
      );
    }
    if (data && !("status" in data)) {
      return (
        <Stack>
          <Text weight={500}>Valideringsresultat:</Text>
          <Code
            block
            style={{
              backgroundColor: "#ccffcc", // Ljus grön bakgrund för framgång
              color: "#006600", // Mörk grön text
              padding: "16px",
              borderRadius: "4px",
              border: "1px solid #00cc00",
            }}
          >
            {JSON.stringify(data, null, 2)}
          </Code>
        </Stack>
      );
    }
    return null;
  };

  return (
    <Box>
      <Modal opened onClose={() => onClose()} title="Last opp Excel-fil for validering" size="lg">
        <Stack>
          <Dropzone
            onDrop={handleDrop}
            accept={{
              "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
              "application/vnd.ms-excel": [".xls"],
            }}
            loading={isLoading}
            disabled={isLoading}
            h={200}
            p={10}
          >
            <Text ta="center">Slipp en Excel-fil her eller klikk for å velge</Text>
          </Dropzone>
          {file && <Text>Valgt fil: {file.name}</Text>}
          <Button onClick={handleSubmit} disabled={!file || isLoading} color="green">
            Valider fil
          </Button>
          {renderValidationResult()}
        </Stack>
      </Modal>
    </Box>
  );
};

const ChecklistExcelImportTest: React.FC = () => {
  const [opened, setOpened] = useState(false);
  return (
    <>
      <Button
        onClick={() => setOpened(true)}
        color="blue"
        leftSection={<i className="fa fa-plus" />}
        w={200} // 200px bredd
      >
        Test excelimport
      </Button>
      {opened === true && <ChecklistExcelImportModal onClose={() => setOpened(false)} />}
    </>
  );
};

export default ChecklistExcelImportTest;
